import React, { useState, useEffect, useCallback } from 'react';
import DirectTradingViewChart from './DirectTradingViewChart';
import SignalCard from './SignalCard';
import NewsPanel from './NewsPanel';
import TechnicalAnalysisPanel from './TechnicalAnalysisPanel';
import { TradingSetup, ChartConfig } from '../types';
import { WebhookSignal } from '../types/webhook';
import { MarketNews } from '../types/marketData';
import { loadTradingSetup, generateCharts, saveTradingSetup } from '../utils/storage';
import { webhookService } from '../services/webhookService';
import { signalGenerator } from '../services/signalGenerator';

const Dashboard = () => {
  const [tradingSetup, setTradingSetup] = useState<TradingSetup>(loadTradingSetup());
  const [showControls, setShowControls] = useState(false);
  const [chartCount, setChartCount] = useState(5);
  const [persistenceEnabled] = useState(true);
  const [resizeMode, setResizeMode] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [chartSizes, setChartSizes] = useState<{[key: string]: {width: number, height: number}}>({});
  const [resizeData, setResizeData] = useState<{
    chartId: string;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } | null>(null);
  const [showSignalCard, setShowSignalCard] = useState(false);
  const [signalCount, setSignalCount] = useState(0);
  const [mockSignalsEnabled, setMockSignalsEnabled] = useState(true);
  const [showNewsPanel, setShowNewsPanel] = useState(false);
  const [showTechnicalAnalysis, setShowTechnicalAnalysis] = useState(false);
  const [selectedSymbol] = useState('BTCUSDT');

  const handleChartConfigChange = useCallback((chartId: string, updates: Partial<ChartConfig>) => {
    // Update state using functional update to avoid dependency on tradingSetup
    setTradingSetup(prevSetup => {
      const updatedCharts = prevSetup.charts.map(chart =>
        chart.id === chartId ? { ...chart, ...updates } : chart
      );
      const updatedSetup = { ...prevSetup, charts: updatedCharts };
      
      // Save to localStorage if persistence is enabled
      if (persistenceEnabled) {
        saveTradingSetup(updatedSetup);
      }
      
      return updatedSetup;
    });
  }, [persistenceEnabled]);

  const handleWebhookSignal = useCallback((signal: WebhookSignal) => {
    console.log('Webhook signal received:', signal);
    
    // Update signal indicators
    setSignalCount(prev => prev + 1);
    
    // Auto-update charts if enabled
    if (signal.symbol) {
      // Use functional update to get current charts
      setTradingSetup(prevSetup => {
        const chartToUpdate = prevSetup.charts.find(chart => 
          chart.symbol === signal.symbol
        );
        
        if (chartToUpdate) {
          const updates: Partial<ChartConfig> = {};
          
          // Update timeframe if provided
          if (signal.timeframe) {
            updates.interval = signal.timeframe;
          }
          
          // Update symbol if different
          if (signal.symbol !== chartToUpdate.symbol) {
            updates.symbol = signal.symbol;
          }
          
          // Apply updates
          if (Object.keys(updates).length > 0) {
            const updatedCharts = prevSetup.charts.map(chart =>
              chart.id === chartToUpdate.id ? { ...chart, ...updates } : chart
            );
            const updatedSetup = { ...prevSetup, charts: updatedCharts };
            
            // Save to localStorage if persistence is enabled
            if (persistenceEnabled) {
              saveTradingSetup(updatedSetup);
            }
            
            return updatedSetup;
          }
        }
        
        return prevSetup;
      });
    }
  }, [persistenceEnabled]);

  const handleNewsClick = useCallback((news: MarketNews) => {
    // Open news in new tab
    window.open(news.url, '_blank');
  }, []);

  // Handle outside clicks to close panels
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Don't close if clicking on buttons or panels themselves
      if (
        target.closest('.control-panel') ||
        target.closest('.signal-card-container') ||
        target.closest('.news-card-container') ||
        target.closest('.technical-analysis-panel') ||
        target.closest('.floating-btn') ||
        target.closest('.bottom-right-controls')
      ) {
        return;
      }

      // Close all panels
      setShowControls(false);
      setShowSignalCard(false);
      setShowNewsPanel(false);
      setShowTechnicalAnalysis(false);
    };

    // Add event listener when any panel is open
    if (showControls || showSignalCard || showNewsPanel || showTechnicalAnalysis) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showControls, showSignalCard, showNewsPanel, showTechnicalAnalysis]);

  useEffect(() => {
    // Load saved setup on component mount
    const savedSetup = loadTradingSetup();
    
    // Load saved layout
    const savedLayout = localStorage.getItem('tradingview-layout');
    let targetChartCount = savedSetup.charts.length;
    
    if (savedLayout) {
      try {
        const layout = JSON.parse(savedLayout);
        targetChartCount = layout.chartCount || savedSetup.charts.length;
      } catch (e) {
        targetChartCount = savedSetup.charts.length;
      }
    }
    
    // Load saved chart sizes
    const savedChartSizes = localStorage.getItem('tradingview-chart-sizes');
    if (savedChartSizes) {
      try {
        const sizes = JSON.parse(savedChartSizes);
        setChartSizes(sizes);
      } catch (e) {
        console.warn('Failed to load chart sizes:', e);
      }
    }
    
    // Generate charts for the target count if needed
    if (targetChartCount > savedSetup.charts.length) {
      const newCharts = generateCharts(targetChartCount);
      const newSetup = {
        ...savedSetup,
        charts: newCharts,
      };
      setTradingSetup(newSetup);
    } else {
      setTradingSetup(savedSetup);
    }
    
    setChartCount(targetChartCount);
    
    // Start webhook service automatically
    const removeListener = webhookService.addListener(handleWebhookSignal);
    
    return () => {
      // Cleanup listener on unmount
      removeListener();
    };
  }, [handleWebhookSignal]);

  const handleChartCountChange = (count: number) => {
    setChartCount(count);
    // Generate new charts for the specified count
    const newCharts = generateCharts(count);
    const newSetup = {
      ...tradingSetup,
      charts: newCharts,
    };
    setTradingSetup(newSetup);
    
    // Reset chart sizes when changing count
    setChartSizes({});
    if (persistenceEnabled) {
      localStorage.removeItem('tradingview-chart-sizes');
    }
    
    // Save layout to localStorage
    if (persistenceEnabled) {
      localStorage.setItem('tradingview-layout', JSON.stringify({ chartCount: count }));
    }
  };



  const handleResizeStart = (chartId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const chartElement = document.getElementById(chartId);
    if (!chartElement) return;
    
    const rect = chartElement.getBoundingClientRect();
    setIsResizing(true);
    setResizeData({
      chartId,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: rect.width,
      startHeight: rect.height,
    });
  };

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !resizeData) return;
    
    const deltaX = e.clientX - resizeData.startX;
    const deltaY = e.clientY - resizeData.startY;
    
    const newWidth = Math.max(200, resizeData.startWidth + deltaX);
    const newHeight = Math.max(150, resizeData.startHeight + deltaY);
    
    // Update the specific chart size
    setChartSizes(prev => ({
      ...prev,
      [resizeData.chartId]: { width: newWidth, height: newHeight }
    }));
  }, [isResizing, resizeData]);

  const saveChartSizes = useCallback((sizes: {[key: string]: {width: number, height: number}}) => {
    if (persistenceEnabled) {
      try {
        localStorage.setItem('tradingview-chart-sizes', JSON.stringify(sizes));
      } catch (e) {
        console.warn('Failed to save chart sizes:', e);
      }
    }
  }, [persistenceEnabled]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setResizeData(null);
    
    // Save chart sizes when resize ends
    saveChartSizes(chartSizes);
  }, [chartSizes, saveChartSizes]);


  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'nw-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const getGridLayout = (count: number) => {
    if (count === 1) {
      return { columns: 1, rows: 1 };
    } else if (count === 2) {
      return { columns: 2, rows: 1 };
    } else if (count === 3) {
      return { columns: 3, rows: 1 };
    } else if (count === 4) {
      return { columns: 2, rows: 2 };
    } else if (count === 5) {
      return { columns: 3, rows: 2, layout: '5-chart' }; // 2 top + 3 bottom
    } else if (count === 6) {
      return { columns: 3, rows: 2 };
    } else if (count === 7) {
      return { columns: 4, rows: 2, layout: '7-chart' }; // 3 top + 4 bottom
    } else if (count === 8) {
      return { columns: 4, rows: 2 };
    } else if (count === 9) {
      return { columns: 3, rows: 3 };
    } else if (count === 10) {
      return { columns: 5, rows: 2, layout: '10-chart' }; // 5 top + 5 bottom
    } else if (count <= 12) {
      return { columns: 4, rows: 3 };
    } else if (count <= 16) {
      return { columns: 4, rows: 4 };
    } else {
      return { columns: 4, rows: Math.ceil(count / 4) };
    }
  };

  const gridLayout = getGridLayout(chartCount);
  const displayCharts = tradingSetup.charts.slice(0, chartCount);

  const isAnyPanelOpen = showControls || showSignalCard || showNewsPanel || showTechnicalAnalysis;

  return (
    <div className={`dashboard ${tradingSetup.theme} ${isAnyPanelOpen ? 'panel-open' : ''}`}>
      {/* Floating Control Panel */}
      <div className="control-overlay">
        {showControls && (
          <div className="control-panel">
            <div className="control-section">
              <div className="control-header">
                <label>Number of Charts:</label>
                <div className="layout-info">
                  Layout: {gridLayout.columns} × {gridLayout.rows}
                </div>
              </div>
              <div className="chart-count-buttons">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 16].map(count => (
                  <button
                    key={count}
                    className={`chart-count-btn ${chartCount === count ? 'active' : ''}`}
                    onClick={() => handleChartCountChange(count)}
                  >
                    {count}
                  </button>
                ))}
              </div>
            </div>


            <div className="control-section resize-section">
              <label>Resize Mode:</label>
              <button
                className={`resize-toggle ${resizeMode ? 'enabled' : 'disabled'}`}
                onClick={() => setResizeMode(!resizeMode)}
              >
                {resizeMode ? 'ON' : 'OFF'}
              </button>
            </div>


            <div className="control-section">
              <label>Chart Controls:</label>
              <div className="chart-controls-list">
                {displayCharts.map((chart, index) => (
                  <div key={chart.id} className="individual-chart-control">
                    <div className="chart-control-header">
                      <span className="chart-label">Chart {index + 1}</span>
                      <div className="preset-buttons">
                        {['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'ADAUSDT'].map(symbol => (
                          <button
                            key={symbol}
                            className="preset-btn"
                            onClick={() => handleChartConfigChange(chart.id, { symbol })}
                            title={`Set to ${symbol}`}
                          >
                            {symbol.replace('USDT', '')}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="chart-control-inputs">
                      <input
                        type="text"
                        value={chart.symbol}
                        onChange={(e) => handleChartConfigChange(chart.id, { symbol: e.target.value })}
                        placeholder="Symbol"
                        className="symbol-input"
                      />
                      <select
                        value={chart.interval}
                        onChange={(e) => handleChartConfigChange(chart.id, { interval: e.target.value })}
                        className="interval-select"
                      >
                        <option value="1">1m</option>
                        <option value="3">3m</option>
                        <option value="5">5m</option>
                        <option value="10">10m</option>
                        <option value="15">15m</option>
                        <option value="30">30m</option>
                        <option value="60">1h</option>
                        <option value="240">4h</option>
                        <option value="D">1D</option>
                        <option value="W">1W</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="control-section">
              <div className="signal-controls">
                <button 
                  className="clear-signals-btn"
                  onClick={() => {
                    webhookService.clearSignalHistory();
                    setSignalCount(0);
                  }}
                  title="Clear all signals and regenerate with new prices"
                >
                  🔄 Clear Signals
                </button>
        <button 
          className="toggle-mock-btn"
          onClick={() => {
            const newState = !mockSignalsEnabled;
            setMockSignalsEnabled(newState);
            signalGenerator.toggleMockSignals(newState);
          }}
          title="Toggle mock signals (disable for real-time only)"
        >
          {mockSignalsEnabled ? '🎭 Mock ON' : '🎭 Mock OFF'}
        </button>
              </div>
            </div>

          </div>
        )}
      </div>

      {/* Bottom Right Floating Buttons */}
      <div className="bottom-right-controls">
        {/* Settings Button */}
        <button 
          className="floating-btn settings-btn"
          onClick={() => setShowControls(!showControls)}
          title="Chart Controls"
        >
          ⚙️
        </button>

        {/* Technical Analysis Button */}
        <button 
          className="floating-btn technical-analysis-btn"
          onClick={() => setShowTechnicalAnalysis(true)}
          title="Technical Analysis"
        >
          📊
        </button>

        {/* Signal Card Toggle Button */}
        <div className="floating-btn-container">
          <button 
            className="floating-btn signal-card-toggle"
            onClick={() => setShowSignalCard(!showSignalCard)}
            title="Trading Signals"
          >
            📊 {signalCount > 0 && <span className="signal-badge">{signalCount}</span>}
          </button>
          
          {showSignalCard && (
            <div className="signal-card-container">
              <SignalCard 
                onSignalClick={handleWebhookSignal} 
                onClose={() => setShowSignalCard(false)}
              />
            </div>
          )}
        </div>

        {/* News Panel Toggle Button */}
        <div className="floating-btn-container">
          <button 
            className="floating-btn news-card-toggle"
            onClick={() => setShowNewsPanel(!showNewsPanel)}
            title={`Market News & Data (${showNewsPanel ? 'ON' : 'OFF'})`}
            style={{ 
              backgroundColor: showNewsPanel ? '#f59e0b' : '#059669',
              border: showNewsPanel ? '2px solid white' : 'none'
            }}
          >
            📰
          </button>
          
          {showNewsPanel && (
            <div className="news-card-container" style={{ 
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
              width: '400px',
              height: '500px',
              border: '2px solid #f59e0b',
              backgroundColor: '#1a1a1a'
            }}>
              <NewsPanel 
                onNewsClick={handleNewsClick} 
                onClose={() => setShowNewsPanel(false)}
              />
            </div>
          )}
        </div>
      </div>

      <div className="charts-grid" data-chart-count={chartCount} data-layout={gridLayout.layout || 'default'}>
        {displayCharts.map((chartConfig, index) => {
          const customSize = chartSizes[chartConfig.id];
          const style = customSize ? {
            width: `${customSize.width}px`,
            height: `${customSize.height}px`,
            flex: 'none'
          } : {};
          
          return (
            <div 
              key={chartConfig.id} 
              className="chart-wrapper" 
              id={`wrapper-${chartConfig.id}`}
              style={style}
            >
              <DirectTradingViewChart
                config={chartConfig}
                theme={tradingSetup.theme}
                timezone={tradingSetup.timezone}
                locale={tradingSetup.locale}
                onConfigChange={(updates: Partial<ChartConfig>) => handleChartConfigChange(chartConfig.id, updates)}
              />
              {resizeMode && (
                <div 
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart(chartConfig.id, e)}
                  title="Drag to resize chart"
                >
                  ↖
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Technical Analysis Panel */}
      {showTechnicalAnalysis && (
        <TechnicalAnalysisPanel
          symbol={selectedSymbol}
          onClose={() => setShowTechnicalAnalysis(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
